/**
 * Page side of the offline shell (worker: `public/sw.js`).
 *
 * Two jobs, both deliberately unable to break a boot:
 *
 *  - register the service worker, so a cold boot with the WAN down serves the
 *    last-good launcher instead of the WebView's error page;
 *  - once a launcher has actually rendered, tell the worker WHICH assets that
 *    render used, so it can commit a shell + assets set that is known to work
 *    together. See design note 4 in `public/sw.js` for why the worker refuses
 *    to promote a bundle it has only half of.
 *
 * Everything here is best-effort. A browser without service workers (an older
 * MediaSuite webview, a CMS preview iframe) simply keeps the pre-existing
 * behaviour: online-only, with the durable config cache in `hooks/useConfig.ts`
 * still covering an API outage on an already-loaded page.
 */

import { hostSearch } from "./hostQuery";

/** File extensions worth keeping in the shell cache. */
const ASSET_RE = /\.(js|mjs|css|woff2?|ttf|otf|svg|png|jpe?g|webp|ico)(\?|$)/i;

/** How long we wait for the worker's commit ack before giving up on it. */
const COMMIT_TIMEOUT_MS = 20_000;

/** Set once per page so a config refresh storm can't re-commit repeatedly. */
let committed = false;

function serviceWorkerContainer(): ServiceWorkerContainer | undefined {
  if (typeof navigator === "undefined") return undefined;
  return navigator.serviceWorker ?? undefined;
}

/**
 * Whether this page should own an offline shell at all.
 *
 * Excluded: the CMS preview surfaces (`?cms=1` / `?preview=1`). They run on the
 * same origin as a real TV, and a worker registered from an operator's browser
 * tab would start answering that operator's navigations from a TV shell cache -
 * which is both confusing and completely useless to them.
 */
export function shouldRegisterOfflineShell(search: string): boolean {
  const params = new URLSearchParams(search);
  return params.get("cms") !== "1" && params.get("preview") !== "1";
}

/**
 * Register the worker. Safe to call unconditionally: a no-op without a service
 * worker container, on the CMS preview surfaces, and (like the guest and
 * relative apps) in dev, where a worker caching a Vite dev server's module
 * graph causes far more confusion than it prevents.
 */
export function registerOfflineShell(): void {
  const container = serviceWorkerContainer();
  if (!container) {
    shellStatus = "unsupported: no navigator.serviceWorker";
    console.warn(`[offline-shell] ${shellStatus}`);
    return;
  }
  if (typeof window === "undefined") return;
  if (!shouldRegisterOfflineShell(hostSearch())) {
    shellStatus = "skipped: CMS preview surface";
    return;
  }
  try {
    void container.register("/sw.js").then(
      () => {
        shellStatus = "registered";
        console.warn("[offline-shell] registered");
      },
      (err: unknown) => {
        shellStatus = `failed: ${err instanceof Error ? err.message : String(err)}`;
        console.warn(`[offline-shell] ${shellStatus}`);
      }
    );
  } catch (err) {
    // Older webviews throw synchronously on an unsupported scope.
    shellStatus = `threw: ${err instanceof Error ? err.message : String(err)}`;
    console.warn(`[offline-shell] ${shellStatus}`);
  }
}

/**
 * What happened to the registration, for the log capture (TIZEN-12).
 *
 * This used to be swallowed by a bare `.catch(() => {})` reasoning that "a
 * failed registration is one less safety net, never a broken TV". True as far
 * as it goes - and it is why the record on TIZEN-12 currently contradicts
 * itself. First light measured the worker registering happily, but that was a
 * page the panel had NAVIGATED to; inside the signed widget the document is the
 * widget's own local origin and `/sw.js` is a cross-origin scope, which is a
 * different answer entirely.
 *
 * "One less safety net" is exactly the thing an operator needs told: without
 * the shell, a WAN outage blanks that room rather than serving the last-good
 * launcher, and on Samsung the substitute is the local asset cache (TIZEN-9)
 * that has not been built yet. So the outcome is now stated rather than
 * discarded, and `log_capture` carries it off the panel.
 */
let shellStatus = "not attempted";

export function offlineShellStatus(): string {
  return shellStatus;
}

/**
 * Same-origin asset URLs this page actually loaded, as path+query keys.
 *
 * Read from the resource timeline rather than the DOM because the DOM does not
 * know about `@font-face` files, dynamically imported chunks, or the legacy
 * SystemJS bundle - all of which the next offline boot needs. The DOM is still
 * consulted as a fallback for webviews with a shallow/absent timeline.
 */
export function collectShellAssets(): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (raw: string | null | undefined) => {
    if (!raw) return;
    let u: URL;
    try {
      u = new URL(raw, window.location.href);
    } catch {
      return;
    }
    if (u.origin !== window.location.origin) return;
    if (u.pathname.startsWith("/api/")) return;
    if (u.pathname.startsWith("/foyer-assets/")) return;
    if (u.pathname === "/sw.js") return;
    if (!ASSET_RE.test(u.pathname)) return;
    const key = u.pathname + u.search;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(key);
  };

  try {
    for (const entry of performance.getEntriesByType("resource")) {
      push(entry.name);
    }
  } catch {
    /* no resource timing on this webview - the DOM sweep below still helps */
  }
  try {
    document
      .querySelectorAll<HTMLScriptElement>("script[src]")
      .forEach((el) => push(el.getAttribute("src")));
    document
      .querySelectorAll<HTMLLinkElement>(
        'link[rel="stylesheet"], link[rel="modulepreload"], link[rel="preload"]'
      )
      .forEach((el) => push(el.getAttribute("href")));
  } catch {
    /* ignore */
  }
  return out;
}

/**
 * Tell the worker this render is good and it may promote it to the last-good
 * shell. Call it when a real launcher is on screen - not when the bundle
 * merely parsed, because a bundle that parses and then throws is exactly the
 * kind of build we must not pin a dark room to.
 *
 * Resolves with whether the worker committed. Never rejects.
 */
export async function commitGoodShell(): Promise<boolean> {
  if (committed) return true;
  const container = serviceWorkerContainer();
  if (!container) return false;
  if (typeof window === "undefined") return false;
  if (!shouldRegisterOfflineShell(hostSearch())) return false;
  committed = true;
  try {
    const registration = await container.ready;
    const worker = registration.active ?? container.controller;
    if (!worker) {
      committed = false;
      return false;
    }
    const assets = collectShellAssets();
    const ok = await new Promise<boolean>((resolve) => {
      const channel = new MessageChannel();
      const timer = setTimeout(() => resolve(false), COMMIT_TIMEOUT_MS);
      channel.port1.onmessage = (event: MessageEvent) => {
        clearTimeout(timer);
        resolve(event.data?.ok === true);
      };
      worker.postMessage({ type: "foyer:shell-ok", assets }, [channel.port2]);
    });
    if (!ok) {
      // Let a later successful render try again - a commit that failed because
      // the network died halfway is precisely the case worth retrying.
      committed = false;
    }
    return ok;
  } catch {
    committed = false;
    return false;
  }
}

/** Test seam - forget that this page already committed. */
export function resetOfflineShellForTests(): void {
  committed = false;
}
